import type { ApolloError, ApolloQueryResult, OperationVariables, TypedDocumentNode } from "@apollo/client";
import { filter } from "ramda";

import { createErrorHandler, type ErrorHandlerProps } from "@/app-utils/errors.ts";
import { client, store } from "@/tools.ts";

/**
 * Query any graphql outside of a Functional Component.
 * This utility already register the error and show an alert if it fails to load.
 * Use the auto-generated hooks from the .graphql files if in a Component.
 * Also @see useError.ts in this case to manage errors.
 */
export function query<Q, V extends OperationVariables, T = unknown>(
  query: TypedDocumentNode<T, V>,
  errorOptions: ErrorHandlerProps = {},
) {
  return async (variables: V) => {
    // The Redux Store is set in @/setup.ts, so it's there already.
    const handleError = createErrorHandler(store()!.dispatch, {
      showAlert: true,
      throwError: false,
      ...errorOptions,
    });

    try {
      // The variables must be all set (Types.Exact), but in GraphQL, optional variables are just unset, so we filter out "null" variables.
      const filteredVars = filter((v) => v != undefined, variables);

      // The Apollo Client is set in @/setup.ts, so it's there already.
      const { data, error }: ApolloQueryResult<Q> = await client()!.query<Q, V>({ query, variables: filteredVars });

      if (error) {
        void handleError(error);
      }

      return {
        data,
        error,
      };
    } catch (error) {
      // Usual error, like network error. Above is GraphQL errors.
      void handleError(error as ApolloError);
      return { data: null, error: error };
    }
  };
}
